import { SelectOptionItemType } from 'components/SelectOption'
import formName from 'constants/formName'
import {
  BalanceType,
  OrderStyleType,
  OrderTifType,
  StateType,
  TicketFormErrorType,
  TicketFormType
} from 'data/types'
import addDays from 'date-fns/add_days'
import messages from 'lang'
import { getFormMeta, getFormSyncErrors, getFormValues } from 'redux-form'
import { createSelector } from 'reselect'
import { SideType } from 'scenes/MarketData/types'
import webSocket from 'services/api/webSocket'

import balance from '../../Balance/selectors'
import instrument from '../../Instrument/selectors'

const initialValues: TicketFormType = {
  animate: false,
  collateralCurrency: 'BTC',
  goodToDate: addDays(new Date(), 30),
  hasStopLoss: false,
  hasTakeProfit: false,
  leverageRatio: 2,
  marginOrder: false,
  side: 'buy',
  tif: webSocket.order.tif.GTC,
  type: webSocket.order.type.MARKET
}

const LIMIT = {
  text: messages.order_type_limit,
  value: webSocket.order.type.LIMIT
}

const MARKET = {
  text: messages.order_type_market,
  value: webSocket.order.type.MARKET
}

const STOP_LIMIT = {
  text: messages.order_type_stop_limit,
  value: webSocket.order.type.STOP_LIMIT
}

const STOP = {
  text: messages.order_type_stop,
  value: webSocket.order.type.STOP
}

const buyTypes = [MARKET, LIMIT, STOP_LIMIT]

const sellTypes = [MARKET, LIMIT, STOP, STOP_LIMIT]

const IOC = {
  text: messages.order_tif_ioc,
  value: webSocket.order.tif.IOC
}

const FOK = {
  text: messages.order_tif_fok,
  value: webSocket.order.tif.FOK
}

const GTC = {
  text: messages.order_tif_gtc,
  value: webSocket.order.tif.GTC
}

const GTD = {
  text: messages.order_tif_gtd,
  value: webSocket.order.tif.GTD
}

const tifByType = {
  limit: [FOK, GTC, GTD, IOC],
  market: [IOC, FOK],
  stop: [GTC, GTD],
  stopLimit: [GTC, GTD]
}

const typesBySide = {
  buy: buyTypes,
  sell: sellTypes
}

const getMeta = (state: StateType) => getFormMeta(formName.TICKET)(state)

const getValues: (state: StateType) => TicketFormType = createSelector(
  // @ts-ignore FIXME fix tying issues with reduxForm
  getFormValues(formName.TICKET),
  (values: void | TicketFormType) => values || initialValues
)

const getSide: (state: StateType) => SideType = createSelector(getValues, (values) => values.side)

// @ts-ignore FIXME fix tying issues with reduxForm
const getErrors: (state: StateType) => TicketFormErrorType = createSelector(
  // @ts-ignore FIXME fix tying issues with reduxForm
  getFormSyncErrors(formName.TICKET),
  getSide,
  balance.getCurrent,
  instrument.getBaseSymbol,
  balance.getCounter,
  instrument.getCounterSymbol,
  (
    formErrors: void | TicketFormErrorType,
    side: SideType,
    baseBalance: void | BalanceType,
    baseSymbol: string,
    counterBalance: void | BalanceType,
    counterSymbol: string
  ) => {
    const errors = formErrors || {}

    const balance = side === 'sell' ? baseBalance : counterBalance
    const symbol = side === 'sell' ? baseSymbol : counterSymbol

    if (!balance || (balance && balance.available === 0)) {
      return {
        balance: {
          id: messages.you_have_zero,
          values: { currency: { symbol } }
        }
      }
    }

    return errors
  }
)

const getTif: (state: StateType) => OrderTifType = createSelector(getValues, (values) => values.tif)

const getTypeItems: (state: StateType) => Array<SelectOptionItemType> = createSelector(
  getSide,
  (side) => typesBySide[side]
)

const getType: (state: StateType) => OrderStyleType = createSelector(
  getValues,
  (values) => values.type
)

const getTifItems: (state: StateType) => Array<SelectOptionItemType> = createSelector(
  getType,
  (type: OrderStyleType) => tifByType[type]
)

export default {
  getErrors,
  getMeta,
  getSide,
  getTif,
  getTifItems,
  getType,
  getTypeItems,
  getValues,
  initialValues,
  tifByType,
  typesBySide
}
